import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-6" };
const _hoisted_2 = {
    key: 0,
    class: "flex items-center gap-2 rounded-xl border border-(--colors-border-base-soft) bg-(--colors-background-white-secondary) dark:bg-(--colors-background-black-secondary) px-4 py-3 text-sm text-(--colors-text-secondary)"
};
const _hoisted_3 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-6" };
const _hoisted_4 = { class: "grid grid-cols-2 gap-x-4" };
const _hoisted_5 = {
    key: 0,
    class: "flex flex-col"
};
const _hoisted_6 = { class: "flex flex-col" };
const _hoisted_7 = { class: "mt-5" };
const _hoisted_8 = { class: "grid grid-cols-2 gap-x-4" };
const _hoisted_9 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-6" };
const _hoisted_10 = { class: "grid grid-cols-1 gap-x-4" };
const _hoisted_11 = { class: "flex flex-col" };
const _hoisted_12 = { class: "flex flex-col" };
const _hoisted_13 = {
    key: 1,
    class: "flex flex-col"
};
const _hoisted_14 = { class: "grid grid-cols-1 lg:grid-cols-2 gap-6" };
const _hoisted_15 = { class: "grid grid-cols-2 gap-x-4" };
import { AutoComplete, DatePickerInput, SelectInput, Textarea, TextInput, WidgetShell, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink } from "vue-router";
import { shownRefusalReason, } from "../../../domain/entities/Donation";
import FinanceEntityPicker from "../FinanceEntityPicker.vue";
import CreateContactModal from "@/modules/contact/presentation/components/CreateContactModal.vue";
import CreateOrganisationModal from "@/modules/organisation/presentation/components/CreateOrganisationModal.vue";
import { displayDateToIso, isoToDisplayDate, toDatePickerFormat, } from "@/shared/utils/date-formatting";
import { useProfileStore } from "../../../../settings/stores/useProfileStore";
import { useDonationOptions } from "../../composables/useDonationOptions";
import { currencyLabel } from "@/shared/utils/workspaceLocaleLabels";
import { donationStatusTheme, donationStatusIcon } from "../../composables/donationStatusBadge";
import { useSubscriptionOptions } from "../../composables/useSubscriptionOptions";
import { useCampaignOptions } from "../../composables/useCampaignOptions";
import { useContactOptions } from "../../composables/useContactOptions";
import { useOrganisationOptions } from "../../composables/useOrganisationOptions";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationTabDetails',
    props: {
        state: {},
        originalStatus: {},
        locked: { type: Boolean },
        availableTransitions: {},
        transitionsLoading: { type: Boolean },
        transitionsError: {},
        originalPaymentMethod: {}
    },
    emits: ["update:state"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { locale, t } = useI18n();
        const profileStore = useProfileStore();
        // Donation date is stored ISO (`yyyy-MM-dd`); OPAL's DatePickerInput speaks the
        // user's display format in both directions. Bridge at the binding boundary — same
        // pattern as DonationCreateModal — so the picker shows e.g. "06/10/2026" while the
        // state stays ISO. Without this (binding the raw value + no `format`) flowbite
        // misparses the stored instant into garbage like "10/06/0178" (OP-5819).
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        const donationDateDisplay = computed({
            get: () => isoToDisplayDate(props.state.donationDate, profileStore.profile?.dateFormat, locale.value),
            set: (display) => emit("update:state", {
                donationDate: displayDateToIso(display, profileStore.profile?.dateFormat, locale.value),
            }),
        });
        const { donationTypeOptions, paymentMethodOptionsFor, isDonationPaymentMethodLocked, donationSourceOptions, } = useDonationOptions();
        const recordPaymentMethodOptions = paymentMethodOptionsFor(() => [
            props.state.paymentMethod,
            props.originalPaymentMethod,
        ]);
        const currencyDisplay = computed(() => props.state.currency ? currencyLabel(props.state.currency, locale.value) : "");
        // Persisted status, not the edited one: the reason is what the provider said about the saved state.
        const refusalReason = computed(() => shownRefusalReason({ status: props.originalStatus, paymentRefusalReason: props.state.paymentRefusalReason }));
        // Selectable statuses = the persisted status plus its backend-valid forward transitions
        // (fetched from OPDON's available-transitions endpoint, not a hardcoded FE map — the
        // prior static matrix silently drifted from the backend, e.g. missing Validated→Refunded until
        // OP-5846 patched it by hand). Gated on `originalStatus` (last saved), not the live
        // `state.status`, so picking an unsaved value doesn't lock out the other valid targets before
        // save.
        const selectableStatusOptions = computed(() => [props.originalStatus, ...props.availableTransitions].map((code) => ({
            value: code,
            label: t(`finance.donations.status.${code}`),
            badgeTheme: donationStatusTheme[code],
            icon: donationStatusIcon[code],
        })));
        // Terminal once transitions have loaded and none are offered (Rejected/Canceled/Refunded today,
        // but this stays backend-driven rather than hardcoding those three). Only a CONFIRMED empty list
        // from a successful fetch counts as terminal — a fetch failure must not read as "this donation
        // has no further transitions"; :disabled covers the error case via `transitionsError` directly,
        // decoupled from this computed.
        const isTerminalStatus = computed(() => !props.transitionsLoading && !props.transitionsError && props.availableTransitions.length === 0);
        // ─── Subscription picker (OP-5175) ────────────────────────────────────────────
        // Shared with the create modal via useSubscriptionOptions. Loaded across all donors (no donorId
        // scope) so every subscription is selectable, matching the create flow — the backend update handler
        // no longer enforces a same-donor guard. The currently-linked subscription is force-included so its
        // label always renders.
        const { options: subscriptionOptions, loading: subscriptionsLoading, load: loadSubscriptions, search: onSubscriptionSearch, } = useSubscriptionOptions();
        // The AutoComplete displays `options.find(o => o.value === modelValue)?.label ?? modelValue`,
        // so binding the raw subscriptionId before its option has loaded makes it render the technical
        // GUID for a frame (OP-5856). Only surface the id once its matching option — and therefore its
        // business label — is present; until then the picker shows its placeholder/loading state, never
        // the raw id. The "view subscription" RouterLink below keeps using `state.subscriptionId` directly.
        const subscriptionPickerValue = computed(() => {
            const id = props.state.subscriptionId;
            if (!id)
                return "";
            return subscriptionOptions.value.some((o) => o.value === id) ? id : "";
        });
        // ─── Campagne picker ──────────────────────────────────────────────────────────
        // Always editable (the campaign is non-fiscal). The current campaign is seeded from the known
        // `campaignName` so its label renders without a by-id fetch. On select, the option label is emitted
        // as `campaignName` so the header title / list refresh immediately and OPDON's snapshot stays in sync.
        const { options: campaignOptions, loading: campaignsLoading, totalCount: campaignTotalCount, searchTotal: campaignSearchTotal, load: loadCampaigns, search: onCampaignSearch, fetchAll: fetchAllCampaigns, prepend: prependCampaign, } = useCampaignOptions();
        // Mirror the subscription picker's GUID-flash guard: only surface the id once its option (and label)
        // is present, otherwise the AutoComplete renders the raw GUID for a frame.
        const campaignPickerValue = computed(() => props.state.campaignId && campaignOptions.value.some((o) => o.value === props.state.campaignId)
            ? props.state.campaignId
            : "");
        // The picker emits the full chosen option (dropdown OR see-all browser); prepend it so its label
        // renders even when it came from the all-results browser (outside the dropdown buffer).
        function onCampaignSelected(option) {
            prependCampaign(option);
            emit("update:state", {
                campaignId: option.value,
                campaignName: option.value ? option.label : "",
            });
        }
        // ─── Donor pickers (re-point the donor) ────────────────────────────────────────
        // Individual → the donor contact; Organization → the donor org plus its referent contact. The
        // current selection is seeded from the known display name. The picked label is emitted alongside the
        // id (`donorName` for the donor, `contactDisplayName` for the referent) so OPDON can recompute the
        // label and snapshot the name while its OPUS snapshot lags (Kafka), and the list/title update at once.
        const { options: contactOptions, loading: contactsLoading, totalCount: contactTotalCount, load: loadContacts, search: onContactSearch, fetchAll: fetchAllContacts, prepend: prependContact, } = useContactOptions();
        const { options: organisationOptions, loading: organisationsLoading, totalCount: organisationTotalCount, load: loadOrganisations, search: onOrganisationSearch, fetchAll: fetchAllOrganisations, prepend: prependOrganisation, } = useOrganisationOptions();
        const contactPickerValue = computed(() => props.state.contactId && contactOptions.value.some((o) => o.value === props.state.contactId)
            ? props.state.contactId
            : "");
        const organisationPickerValue = computed(() => props.state.organizationId &&
            organisationOptions.value.some((o) => o.value === props.state.organizationId)
            ? props.state.organizationId
            : "");
        // Inline required-field errors shown under each picker when the user clears a mandatory reference
        // (the donor contact / referent, the donor org, the campaign). The parent's save gate also blocks
        // the save, but the message tells the user which field to fix. Reuses the create-modal validation keys.
        const contactError = computed(() => props.state.contactId ? "" : t("finance.donations.validation.contactRequired"));
        const organisationError = computed(() => props.state.organizationId ? "" : t("finance.donations.validation.donorRequired"));
        const campaignError = computed(() => props.state.campaignId ? "" : t("finance.donations.validation.campaignRequired"));
        // Individual donation: the donor IS the picked contact, so its label is the donor name.
        function onDonorContactSelected(option) {
            prependContact(option);
            emit("update:state", { contactId: option.value, donorName: option.value ? option.label : "" });
        }
        // Organization donation: re-point the donor org. Its label is the donor name. The referent must
        // belong to the newly-picked org, so clear the stale referent and reload the org-scoped contact list;
        // the parent's save gate then blocks saving until a referent is re-picked.
        function onOrganisationSelected(option) {
            prependOrganisation(option);
            emit("update:state", {
                organizationId: option.value,
                donorName: option.value ? option.label : "",
                contactId: "",
                contactDisplayName: "",
            });
            if (option.value)
                loadContacts({ organisationId: option.value, force: true });
        }
        // Organization donation: the referent contact. Its label is sent as `contactDisplayName`.
        function onReferentSelected(option) {
            prependContact(option);
            emit("update:state", {
                contactId: option.value,
                contactDisplayName: option.value ? option.label : "",
            });
        }
        // ─── Inline "create new" donor / referent / organisation (picker "+ Créer" footer) ─────────────
        // Lazily mounted nested modals. The new record re-points the donor (Individual) / sets the referent
        // (Organization) / becomes the donor org. We seed the picker option so the name renders, then emit
        // the matching state update — no refetch needed.
        const showCreateContactModal = ref(false);
        const createContactModalMounted = ref(false);
        const showCreateOrgModal = ref(false);
        const createOrgModalMounted = ref(false);
        const createOrgInitialName = ref("");
        // For an Organization donation the new contact must link to the donor org (the nested modal forces
        // the Professional type + the contact↔org relation); Individual donations have no org context.
        const contactModalOrgId = computed(() => props.state.donorType === "Organization" ? props.state.organizationId || undefined : undefined);
        watch(showCreateContactModal, (open) => {
            if (open)
                createContactModalMounted.value = true;
        });
        watch(showCreateOrgModal, (open) => {
            if (open)
                createOrgModalMounted.value = true;
        });
        function openCreateContact() {
            showCreateContactModal.value = true;
        }
        function openCreateOrganisation(query) {
            createOrgInitialName.value = query;
            showCreateOrgModal.value = true;
        }
        function onContactCreated(newId, _relationTitle, name) {
            showCreateContactModal.value = false;
            const label = name ? `${name.firstName} ${name.lastName}`.trim() || newId : newId;
            prependContact({ value: newId, label });
            if (props.state.donorType === "Organization") {
                emit("update:state", { contactId: newId, contactDisplayName: label });
            }
            else {
                emit("update:state", { contactId: newId, donorName: label });
            }
        }
        function onOrganisationCreated(newId, name) {
            showCreateOrgModal.value = false;
            const label = name?.trim() || newId;
            prependOrganisation({ value: newId, label });
            emit("update:state", {
                organizationId: newId,
                donorName: label,
                contactId: "",
                contactDisplayName: "",
            });
            loadContacts({ organisationId: newId, force: true });
        }
        onMounted(() => {
            // Only Regular donations expose the subscription picker, so the options are only needed then.
            if (props.state.donationType === "Regular") {
                loadSubscriptions({ ensureSelectedId: props.state.subscriptionId });
            }
            loadCampaigns({
                ensureSelectedId: props.state.campaignId,
                ensureSelectedLabel: props.state.campaignName,
            });
            if (props.state.donorType === "Organization") {
                loadOrganisations({
                    ensureSelectedId: props.state.organizationId,
                    ensureSelectedLabel: props.state.donorName,
                });
                loadContacts({
                    organisationId: props.state.organizationId,
                    ensureSelectedId: props.state.contactId,
                    ensureSelectedLabel: props.state.contactDisplayName,
                });
            }
            else {
                loadContacts({
                    ensureSelectedId: props.state.contactId,
                    ensureSelectedLabel: props.state.donorName,
                });
            }
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (__props.locked)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _cache[10] || (_cache[10] = _createElementVNode("span", {
                            class: "ri-lock-line text-base shrink-0",
                            "aria-hidden": "true"
                        }, null, -1 /* CACHED */)),
                        _createTextVNode(" " + _toDisplayString(_ctx.$t("finance.donations.taxReceipt.lockedByReceipt")), 1 /* TEXT */)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(WidgetShell), {
                        header: _ctx.$t('finance.donations.detail.generalInfo')
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_4, [
                                _createVNode(_unref(TextInput), {
                                    label: _ctx.$t('finance.donations.detail.idDon'),
                                    "model-value": __props.state.id,
                                    disabled: true,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["label", "model-value"]),
                                _createVNode(_unref(SelectInput), {
                                    "model-value": __props.state.donationType,
                                    label: _ctx.$t('finance.donations.fields.donationType'),
                                    options: _unref(donationTypeOptions),
                                    disabled: true,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["model-value", "label", "options"]),
                                (__props.state.donationType === 'Regular')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                        _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                            "model-value": subscriptionPickerValue.value,
                                            label: _ctx.$t('finance.donations.fields.subscriptionId'),
                                            placeholder: _ctx.$t('finance.donations.fields.subscriptionIdPlaceholder'),
                                            options: _unref(subscriptionOptions),
                                            loading: _unref(subscriptionsLoading),
                                            required: true,
                                            disabled: __props.locked,
                                            "icon-left": '',
                                            "icon-right": '',
                                            "data-testid": "donation-detail-subscription-picker",
                                            "full-width": "",
                                            onSearch: _unref(onSubscriptionSearch),
                                            "onUpdate:modelValue": _cache[0] || (_cache[0] =
                                                (v) => emit('update:state', { subscriptionId: v ?? '' }))
                                        }), null, 16 /* FULL_PROPS */, ["model-value", "label", "placeholder", "options", "loading", "disabled", "onSearch"]),
                                        (__props.state.subscriptionId)
                                            ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                                key: 0,
                                                to: { name: 'SubscriptionDetail', params: { subscriptionId: __props.state.subscriptionId } },
                                                class: "-mt-3 mb-2 inline-flex w-fit items-center gap-1 text-sm font-medium text-(--text-text-link) hover:text-(--text-text-hover) transition-colors",
                                                "data-testid": "donation-detail-subscription-link"
                                            }, {
                                                default: _withCtx(() => [
                                                    _cache[11] || (_cache[11] = _createElementVNode("i", { class: "ri-external-link-line" }, null, -1 /* CACHED */)),
                                                    _createTextVNode(" " + _toDisplayString(_ctx.$t("finance.donations.detail.viewSubscription")), 1 /* TEXT */)
                                                ]),
                                                _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["to"]))
                                            : _createCommentVNode("v-if", true)
                                    ]))
                                    : _createCommentVNode("v-if", true),
                                _createVNode(_unref(SelectInput), {
                                    "model-value": __props.state.status,
                                    label: _ctx.$t('finance.donations.detail.statusLabel'),
                                    options: selectableStatusOptions.value,
                                    disabled: __props.locked || __props.transitionsLoading || !!__props.transitionsError || isTerminalStatus.value,
                                    error: __props.transitionsError ? _ctx.$t('finance.donations.detail.statusTransitionsLoadError') : undefined,
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (emit('update:state', { status: $event })))
                                }, null, 8 /* PROPS */, ["model-value", "label", "options", "disabled", "error"]),
                                (refusalReason.value)
                                    ? (_openBlock(), _createBlock(_unref(TextInput), {
                                        key: 1,
                                        "model-value": _ctx.$t(`finance.donations.refusalReason.${refusalReason.value}`),
                                        label: _ctx.$t('finance.donations.detail.refusalReasonLabel'),
                                        readonly: "",
                                        "full-width": "",
                                        "data-testid": "donation-detail-refusal-reason"
                                    }, null, 8 /* PROPS */, ["model-value", "label"]))
                                    : _createCommentVNode("v-if", true),
                                _createElementVNode("div", _hoisted_6, [
                                    _createVNode(FinanceEntityPicker, {
                                        "model-value": campaignPickerValue.value,
                                        label: _ctx.$t('finance.donations.fields.campaignLinked'),
                                        placeholder: _ctx.$t('finance.donations.fields.campaignPlaceholder'),
                                        options: _unref(campaignOptions),
                                        loading: _unref(campaignsLoading),
                                        error: campaignError.value,
                                        "total-count": _unref(campaignTotalCount),
                                        "search-total-count": _unref(campaignSearchTotal),
                                        "fetch-all-results": _unref(fetchAllCampaigns),
                                        "data-testid": "donation-detail-campaign-picker",
                                        onSearch: _unref(onCampaignSearch),
                                        onSelect: onCampaignSelected
                                    }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "options", "loading", "error", "total-count", "search-total-count", "fetch-all-results", "onSearch"]),
                                    (__props.state.campaignId)
                                        ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                            key: 0,
                                            to: { name: 'CampaignDetail', params: { id: __props.state.campaignId } },
                                            class: "-mt-3 mb-2 inline-flex w-fit items-center gap-1 text-sm font-medium text-(--text-text-link) hover:text-(--text-text-hover) transition-colors",
                                            "data-testid": "donation-detail-campaign-link"
                                        }, {
                                            default: _withCtx(() => [
                                                _cache[12] || (_cache[12] = _createElementVNode("i", { class: "ri-external-link-line" }, null, -1 /* CACHED */)),
                                                _createTextVNode(" " + _toDisplayString(_ctx.$t("finance.donations.detail.viewCampaign")), 1 /* TEXT */)
                                            ]),
                                            _: 1 /* STABLE */
                                        }, 8 /* PROPS */, ["to"]))
                                        : _createCommentVNode("v-if", true)
                                ]),
                                _createVNode(_unref(SelectInput), {
                                    "model-value": __props.state.donationSource,
                                    label: _ctx.$t('finance.donations.fields.donationSource'),
                                    options: _unref(donationSourceOptions),
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => (emit('update:state', { donationSource: $event })))
                                }, null, 8 /* PROPS */, ["model-value", "label", "options"])
                            ]),
                            _createElementVNode("div", _hoisted_7, [
                                _createVNode(_unref(Textarea), {
                                    "model-value": __props.state.notes ?? '',
                                    label: _ctx.$t('finance.donations.detail.note'),
                                    placeholder: _ctx.$t('finance.donations.detail.notePlaceholder'),
                                    rows: 4,
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (emit('update:state', { notes: $event })))
                                }, null, 8 /* PROPS */, ["model-value", "label", "placeholder"])
                            ])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header"]),
                    _createVNode(_unref(WidgetShell), {
                        header: _ctx.$t('finance.donations.detail.amountAndCurrency')
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_8, [
                                _createVNode(_unref(TextInput), {
                                    label: _ctx.$t('finance.donations.detail.netAmountLabel'),
                                    "model-value": String(__props.state.amount),
                                    disabled: __props.locked,
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[4] || (_cache[4] =
                                        (v) => {
                                            const n = parseFloat(v);
                                            if (!isNaN(n))
                                                emit('update:state', { amount: n });
                                        })
                                }, null, 8 /* PROPS */, ["label", "model-value", "disabled"]),
                                _createVNode(_unref(TextInput), {
                                    "model-value": currencyDisplay.value,
                                    label: _ctx.$t('finance.donations.detail.currencyLabel'),
                                    readonly: "",
                                    "full-width": "",
                                    "data-testid": "donation-detail-currency"
                                }, null, 8 /* PROPS */, ["model-value", "label"])
                            ])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header"])
                ]),
                _createElementVNode("div", _hoisted_9, [
                    _createVNode(_unref(WidgetShell), {
                        header: _ctx.$t('finance.donations.sections.donor')
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_10, [
                                (__props.state.donorType === 'Organization')
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createElementVNode("div", _hoisted_11, [
                                            _createVNode(FinanceEntityPicker, {
                                                "model-value": organisationPickerValue.value,
                                                label: _ctx.$t('finance.donations.fields.orgName'),
                                                placeholder: _ctx.$t('finance.donations.fields.orgNamePlaceholder'),
                                                options: _unref(organisationOptions),
                                                loading: _unref(organisationsLoading),
                                                error: organisationError.value,
                                                required: true,
                                                disabled: __props.locked,
                                                "total-count": _unref(organisationTotalCount),
                                                "fetch-all-results": _unref(fetchAllOrganisations),
                                                "create-label-key": "finance.donations.create.createAsNewOrganisation",
                                                "data-testid": "donation-detail-org-picker",
                                                onSearch: _unref(onOrganisationSearch),
                                                onSelect: onOrganisationSelected,
                                                onCreate: openCreateOrganisation
                                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "options", "loading", "error", "disabled", "total-count", "fetch-all-results", "onSearch"]),
                                            (__props.state.organizationId)
                                                ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                                    key: 0,
                                                    to: { name: 'Organisation', params: { id: __props.state.organizationId } },
                                                    class: "-mt-3 mb-2 inline-flex w-fit items-center gap-1 text-sm font-medium text-(--text-text-link) hover:text-(--text-text-hover) transition-colors",
                                                    "data-testid": "donation-detail-org-link"
                                                }, {
                                                    default: _withCtx(() => [
                                                        _cache[13] || (_cache[13] = _createElementVNode("i", { class: "ri-external-link-line" }, null, -1 /* CACHED */)),
                                                        _createTextVNode(" " + _toDisplayString(_ctx.$t("finance.donations.detail.viewOrganisation")), 1 /* TEXT */)
                                                    ]),
                                                    _: 1 /* STABLE */
                                                }, 8 /* PROPS */, ["to"]))
                                                : _createCommentVNode("v-if", true)
                                        ]),
                                        _createElementVNode("div", _hoisted_12, [
                                            _createVNode(FinanceEntityPicker, {
                                                "model-value": contactPickerValue.value,
                                                label: _ctx.$t('finance.donations.fields.donorContact'),
                                                placeholder: _ctx.$t('finance.donations.fields.contactNamePlaceholder'),
                                                options: _unref(contactOptions),
                                                loading: _unref(contactsLoading),
                                                error: contactError.value,
                                                required: true,
                                                disabled: __props.locked,
                                                "total-count": _unref(contactTotalCount),
                                                "fetch-all-results": _unref(fetchAllContacts),
                                                "create-label-key": "finance.donations.create.createAsNewContact",
                                                "data-testid": "donation-detail-referent-picker",
                                                onSearch: _unref(onContactSearch),
                                                onSelect: onReferentSelected,
                                                onCreate: openCreateContact
                                            }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "options", "loading", "error", "disabled", "total-count", "fetch-all-results", "onSearch"]),
                                            (__props.state.contactId)
                                                ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                                    key: 0,
                                                    to: { name: 'ContactDetail', params: { id: __props.state.contactId } },
                                                    class: "-mt-3 mb-2 inline-flex w-fit items-center gap-1 text-sm font-medium text-(--text-text-link) hover:text-(--text-text-hover) transition-colors",
                                                    "data-testid": "donation-detail-referent-link"
                                                }, {
                                                    default: _withCtx(() => [
                                                        _cache[14] || (_cache[14] = _createElementVNode("i", { class: "ri-external-link-line" }, null, -1 /* CACHED */)),
                                                        _createTextVNode(" " + _toDisplayString(_ctx.$t("finance.donations.detail.viewContact")), 1 /* TEXT */)
                                                    ]),
                                                    _: 1 /* STABLE */
                                                }, 8 /* PROPS */, ["to"]))
                                                : _createCommentVNode("v-if", true)
                                        ])
                                    ], 64 /* STABLE_FRAGMENT */))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                        _createVNode(FinanceEntityPicker, {
                                            "model-value": contactPickerValue.value,
                                            label: _ctx.$t('finance.donations.fields.donorContact'),
                                            placeholder: _ctx.$t('finance.donations.fields.contactNamePlaceholder'),
                                            options: _unref(contactOptions),
                                            loading: _unref(contactsLoading),
                                            error: contactError.value,
                                            required: true,
                                            disabled: __props.locked,
                                            "total-count": _unref(contactTotalCount),
                                            "fetch-all-results": _unref(fetchAllContacts),
                                            "create-label-key": "finance.donations.create.createAsNewContact",
                                            "data-testid": "donation-detail-contact-picker",
                                            onSearch: _unref(onContactSearch),
                                            onSelect: onDonorContactSelected,
                                            onCreate: openCreateContact
                                        }, null, 8 /* PROPS */, ["model-value", "label", "placeholder", "options", "loading", "error", "disabled", "total-count", "fetch-all-results", "onSearch"]),
                                        (__props.state.contactId)
                                            ? (_openBlock(), _createBlock(_unref(RouterLink), {
                                                key: 0,
                                                to: { name: 'ContactDetail', params: { id: __props.state.contactId } },
                                                class: "-mt-3 mb-2 inline-flex w-fit items-center gap-1 text-sm font-medium text-(--text-text-link) hover:text-(--text-text-hover) transition-colors",
                                                "data-testid": "donation-detail-contact-link"
                                            }, {
                                                default: _withCtx(() => [
                                                    _cache[15] || (_cache[15] = _createElementVNode("i", { class: "ri-external-link-line" }, null, -1 /* CACHED */)),
                                                    _createTextVNode(" " + _toDisplayString(_ctx.$t("finance.donations.detail.viewContact")), 1 /* TEXT */)
                                                ]),
                                                _: 1 /* STABLE */
                                            }, 8 /* PROPS */, ["to"]))
                                            : _createCommentVNode("v-if", true)
                                    ]))
                            ])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header"])
                ]),
                _createElementVNode("div", _hoisted_14, [
                    _createVNode(_unref(WidgetShell), {
                        header: _ctx.$t('finance.donations.detail.payment')
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_15, [
                                _createVNode(_unref(SelectInput), {
                                    "model-value": __props.state.paymentMethod,
                                    label: _ctx.$t('finance.donations.fields.paymentMethod'),
                                    options: _unref(recordPaymentMethodOptions),
                                    disabled: __props.locked ||
                                        _unref(isDonationPaymentMethodLocked)(__props.state.paymentMethod, __props.state.externalTransactionProvider),
                                    "full-width": "",
                                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => (emit('update:state', { paymentMethod: $event })))
                                }, null, 8 /* PROPS */, ["model-value", "label", "options", "disabled"]),
                                _createVNode(_unref(DatePickerInput), {
                                    modelValue: donationDateDisplay.value,
                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((donationDateDisplay).value = $event)),
                                    label: _ctx.$t('finance.donations.fields.donationDate'),
                                    format: dateFormat.value,
                                    language: _unref(locale),
                                    "week-start": 1,
                                    disabled: __props.locked,
                                    "full-width": ""
                                }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "disabled"]),
                                (__props.state.paymentMethod === 'Cheque')
                                    ? (_openBlock(), _createBlock(_unref(TextInput), {
                                        key: 0,
                                        label: _ctx.$t('finance.donations.fields.chequeNumber'),
                                        "model-value": __props.state.chequeNumber ?? '',
                                        disabled: __props.locked,
                                        "full-width": "",
                                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => (emit('update:state', { chequeNumber: $event })))
                                    }, null, 8 /* PROPS */, ["label", "model-value", "disabled"]))
                                    : _createCommentVNode("v-if", true)
                            ])
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["header"])
                ]),
                (createContactModalMounted.value)
                    ? (_openBlock(), _createBlock(CreateContactModal, {
                        key: 1,
                        modelValue: showCreateContactModal.value,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((showCreateContactModal).value = $event)),
                        "redirect-on-create": false,
                        "initial-organisation-id": contactModalOrgId.value,
                        "force-professional-type": __props.state.donorType === 'Organization',
                        onCreated: onContactCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-organisation-id", "force-professional-type"]))
                    : _createCommentVNode("v-if", true),
                (createOrgModalMounted.value)
                    ? (_openBlock(), _createBlock(CreateOrganisationModal, {
                        key: 2,
                        modelValue: showCreateOrgModal.value,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((showCreateOrgModal).value = $event)),
                        "redirect-on-create": false,
                        "initial-name": createOrgInitialName.value,
                        onCreated: onOrganisationCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-name"]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
